import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Geometry, per breakpoint.
 *
 * The numbers are not free — three constraints have to hold at once:
 *
 *   1. Icons must not overlap along their own ring.
 *      An icon needs roughly its own width of arc: 2*PI*radius / count >= size.
 *   2. The two rings must not collide radially.
 *      radius - innerRadius > size/2 + innerSize/2.
 *   3. The innermost ring must clear the card sitting in the middle.
 *      innerRadius - innerSize/2 > the card's half-DIAGONAL.
 *
 * Constraint (3) is against the diagonal and not the half-width, which is the
 * trap. The widest card is 256x155, so its corner is 150px from the middle
 * while its side is only 128px away. Size a ring against the width and it
 * clears cleanly at three and nine o'clock and cuts the corners off everywhere
 * else — and whether that is visible depends on where the icons happen to
 * land, which changes with every filter. It has to be the diagonal or it is
 * luck.
 *
 * CARD_HEIGHT is measured, not assumed, and is deliberately the worst case:
 * ~120px with a one-line description, ~145px once the description wraps to the
 * two lines line-clamp-2 allows. Only the width varies by breakpoint — the
 * content is the same everywhere — so one constant covers all four.
 *
 * At 320px no radius satisfies (3) and still fits on screen — clearing a
 * 176x155 card needs a 358px orbit inside 272px of usable width. That is a
 * real limit of putting a ring around a card, not a tuning problem, so xs
 * drops the card out of the middle and sets it below the orbit instead. The
 * middle then holds the active logo at size, which is a better use of a phone
 * screen than a card squeezed to nine-point type.
 */
const CARD_HEIGHT = 145;

const LAYOUT = {
  xs: {
    radius: 118,
    size: 34,
    cardWidth: "w-44",
    cardPx: 176,
    twoRings: false,
    cardInside: false,
  },
  sm: {
    radius: 165,
    size: 42,
    cardWidth: "w-52",
    cardPx: 208,
    twoRings: false,
    cardInside: true,
  },
  md: {
    radius: 218,
    size: 46,
    cardWidth: "w-56",
    cardPx: 224,
    twoRings: true,
    innerRadius: 162,
    innerSize: 36,
    cardInside: true,
  },
  lg: {
    radius: 240,
    size: 52,
    cardWidth: "w-64",
    cardPx: 256,
    twoRings: true,
    innerRadius: 178,
    innerSize: 40,
    cardInside: true,
  },
};

/** Seconds per revolution. The inner ring is slower as well as reversed, so
 *  the two never line up into a single apparent rotation. */
const OUTER_DURATION = 70;
const INNER_DURATION = 95;

/** Below this, one ring is not crowded and splitting it just looks sparse. */
const TWO_RING_THRESHOLD = 10;

/** Past this a logo stops being recognisable, so crowding is the lesser evil. */
const MIN_ICON_SIZE = 22;

/**
 * Density pass on top of the breakpoint LAYOUT.
 *
 * The table sizes for a full ring of ~30 icons. A filtered set of 1–3 leaves a
 * huge empty disc with one logo on the rim — geometry, not styling. Scale the
 * ring down for sparse/medium counts; keep the full two-ring layout at 10+.
 *
 * Sparse radii are floored against the card's half-diagonal when the card sits
 * inside the ring, otherwise a 55% shrink clips the card corners.
 */
function densifyLayout(base, count) {
  if (count <= 3) {
    const bumpedSize = Math.round(base.size * 1.25);
    const targetRadius = Math.round(base.radius * 0.55);
    let radius = targetRadius;
    if (base.cardInside) {
      const cardDiagonal = Math.hypot(base.cardPx / 2, CARD_HEIGHT / 2);
      const minForCard = Math.ceil(cardDiagonal + bumpedSize / 2 + 8);
      radius = Math.max(targetRadius, minForCard);
    }
    return {
      ...base,
      radius,
      size: bumpedSize,
      twoRings: false,
      spin: false,
    };
  }

  if (count < TWO_RING_THRESHOLD) {
    return {
      ...base,
      radius: Math.round(base.radius * 0.8),
      twoRings: false,
      spin: true,
    };
  }

  return { ...base, spin: true };
}

/**
 * The largest icon that still gets its own stretch of the ring.
 *
 * An icon needs about its own width of arc to sit clear of its neighbours, and
 * the arc each one gets is the circumference divided by the count. Taking the
 * smaller of that and the breakpoint's nominal size means a sparse ring keeps
 * full-size icons and a crowded one shrinks to fit instead of overlapping.
 */
function fitIconSize(count, radius, maxSize) {
  if (!count) return maxSize;
  // The 5px is breathing room. Sized to exactly the arc, a full ring measures
  // as "not overlapping" and still reads as a solid band of touching tiles.
  const arcPerIcon = (2 * Math.PI * radius) / count - 5;
  return Math.round(Math.max(MIN_ICON_SIZE, Math.min(maxSize, arcPerIcon)));
}

const useResponsive = () => {
  const [screenSize, setScreenSize] = React.useState("lg");

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 480) setScreenSize("xs");
      else if (width < 640) setScreenSize("sm");
      else if (width < 768) setScreenSize("md");
      else setScreenSize("lg");
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return screenSize;
};

/**
 * One ring of icons.
 *
 * Three nested elements per icon, and each level exists for a reason:
 *   - the slot carries the static rotate+translate that puts it on the ring
 *   - the un-rotate cancels that same angle so the icon is not tipped over
 *   - .orbit-upright cancels the parent ring's *animated* rotation
 * The last two cannot be merged: one is a fixed transform and the other is a
 * running animation, and an element only gets one transform.
 *
 * MOUNT THIS WITH A key DERIVED FROM ITS OWN ITEMS. The upright animation only
 * cancels the ring's rotation while the two share a start time, and a CSS
 * animation's clock starts when its element mounts. Change the filter and the
 * ring element survives — still turning, tens of degrees in — while React
 * mounts fresh icons whose counter-rotation begins at zero. Every new icon is
 * then permanently tilted by however long the ring had been running, which at
 * half a revolution looks exactly like the logos have been flipped. Keying the
 * ring on its own contents makes parent and children mount in the same tick,
 * which is the only thing that keeps the two clocks together.
 */
function OrbitRing({
  items,
  radius,
  size,
  duration,
  reverse,
  spin,
  activeId,
  onSelect,
  stagger = false,
}) {
  const step = 360 / items.length;
  // Both rings hold the same count and would otherwise start every icon at the
  // same angle as its opposite number on the other ring, which lines them up
  // into radial spokes and reads as one zigzag band instead of two rings.
  // Half a step puts the inner icons in the gaps between the outer ones.
  const offset = stagger ? step / 2 : 0;

  return (
    <div
      className={`pointer-events-none absolute inset-0 ${spin ? "orbit-ring" : ""}`}
      data-reverse={reverse ? "true" : undefined}
      style={{ "--orbit-duration": `${duration}s` }}
    >
      {items.map((item, i) => {
        const angle = i * step + offset;
        const isActive = item.id === activeId;
        const IconComponent = item.icon;

        return (
          <div
            key={item.id}
            style={{
              position: "absolute",
              width: size,
              height: size,
              top: `calc(50% - ${size / 2}px)`,
              left: `calc(50% - ${size / 2}px)`,
              transform: `rotate(${angle}deg) translateY(-${radius}px)`,
              zIndex: isActive ? 2 : 1,
            }}
          >
            <div
              className="h-full w-full"
              style={{ transform: `rotate(${-angle}deg)` }}
            >
              <div className={`h-full w-full ${spin ? "orbit-upright" : ""}`}>
                <button
                  type="button"
                  onClick={() => onSelect(item.id)}
                  title={item.name}
                  aria-label={item.name}
                  aria-pressed={isActive}
                  className={`pointer-events-auto flex h-full w-full cursor-pointer items-center justify-center rounded-xl p-2.5 transition-colors focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] ${
                    isActive
                      ? "bg-[var(--bg-elevated)] shadow-[var(--shadow-md)]"
                      : "bg-[var(--bg-canvas)] shadow-[var(--border-ring)] hover:bg-[var(--bg-hover)]"
                  }`}
                >
                  {IconComponent && (
                    <IconComponent className="h-full w-full text-[var(--fg-primary)]" />
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OrbitCarousel({ items = [], autoPlayInterval = 4000 }) {
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isHovering, setIsHovering] = React.useState(false);
  const [onScreen, setOnScreen] = React.useState(false);
  const [scale, setScale] = React.useState(1);
  // Set the first time the reader picks something. From then on the rings stay
  // still: they were there to draw the eye in, and the eye has arrived.
  const [engaged, setEngaged] = React.useState(false);

  const containerRef = React.useRef(null);
  const screenSize = useResponsive();
  const reduceMotion = useReducedMotion();

  const layout = densifyLayout(LAYOUT[screenSize], items.length);
  const { radius, size, cardWidth, cardPx, cardInside } = layout;

  const useTwoRings = layout.twoRings && items.length >= TWO_RING_THRESHOLD;
  const innerRadius = useTwoRings ? layout.innerRadius : 0;

  // Alternating rather than first-half/second-half, so a filtered set does not
  // put every frontend logo on one ring and leave the other looking like an
  // afterthought.
  const outerItems = useTwoRings ? items.filter((_, i) => i % 2 === 0) : items;
  const innerItems = useTwoRings ? items.filter((_, i) => i % 2 === 1) : [];

  // Splitting across two rings halves the density, but below 640px there is no
  // room for a second ring and all thirty icons land on one small circle. The
  // ring cannot grow — it is already at the width of the phone — so the icons
  // give way instead. Every ring gets the largest size that still leaves each
  // icon its own arc, which also means no future count can quietly overcrowd a
  // ring that happens to fit today.
  const outerSize = fitIconSize(outerItems.length, radius, size);
  const innerSize = useTwoRings
    ? fitIconSize(innerItems.length, innerRadius, layout.innerSize)
    : 0;

  // Only half an icon sticks out past the ring, so the container needs the
  // diameter plus one icon plus a little air — not plus two icons.
  const containerSize = radius * 2 + outerSize + 40;

  // The disc behind the middle, stopping short of whichever ring is innermost.
  // It only has to cover the card when the card is actually in there.
  const discRadius = useTwoRings ? innerRadius : radius;
  const discIcon = useTwoRings ? innerSize : outerSize;
  const discSize = Math.max(
    discRadius * 2 - discIcon - 24,
    cardInside ? cardPx : 0,
  );

  // Constraint (3) from the table above, checked rather than trusted. It is
  // the one that fails silently — a corner clipped at one filter and clear at
  // the next — so it should not survive to a screenshot.
  if (import.meta.env?.DEV && cardInside) {
    const clearance = discRadius - discIcon / 2;
    const cardDiagonal = Math.hypot(cardPx / 2, CARD_HEIGHT / 2);
    if (clearance < cardDiagonal) {
      console.warn(
        `OrbitCarousel: at "${screenSize}" the innermost ring clears ${Math.round(clearance)}px ` +
          `but the card's corner reaches ${Math.round(cardDiagonal)}px. Icons will cut it.`,
      );
    }
  }

  const ringKey = (ringItems) => ringItems.map((item) => item.id).join("|");

  const safeIndex = activeIndex >= items.length ? 0 : activeIndex;
  const activeItem = items[safeIndex];

  const next = React.useCallback(
    () => setActiveIndex((i) => (i + 1) % items.length),
    [items.length],
  );
  const prev = React.useCallback(
    () => setActiveIndex((i) => (i - 1 + items.length) % items.length),
    [items.length],
  );

  const select = React.useCallback((index) => {
    setEngaged(true);
    setActiveIndex(index);
  }, []);

  const selectById = React.useCallback(
    (id) => {
      const index = items.findIndex((item) => item.id === id);
      if (index >= 0) select(index);
    },
    [items, select],
  );

  // items is rebuilt inline by the parent on every one of its renders, so it
  // can never be a dependency directly — reset on what it contains instead.
  const itemsKey = items.map((item) => item.id).join("|");
  React.useEffect(() => {
    setActiveIndex(0);
    // A new filter is a new view, and it gets its own drift before settling.
    setEngaged(false);
  }, [itemsKey]);

  // Stop the compositor work when nobody is looking, and give the keyboard
  // handler below something to scope itself to.
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      {
        threshold: 0,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The orbit is laid out at a fixed pixel size, so on anything narrower than
  // its own diameter it would push the page sideways. Measuring the space
  // actually available and scaling to fit covers every width rather than the
  // four the breakpoint table knows about — a 320px phone, a narrow split
  // window, a zoomed-in page. Scaling down is safe here because nothing in the
  // orbit carries text; the card in the middle keeps its own size.
  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const available = entry.contentRect.width;
      setScale(available > 0 ? Math.min(1, available / containerSize) : 1);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [containerSize]);

  // Sparse rings (1–3) stay still — one logo circling an empty disc looks unfinished.
  // Same effect as engaged; densifyLayout sets spin: false for that case.
  const paused =
    isHovering || engaged || !onScreen || reduceMotion || !layout.spin;

  React.useEffect(() => {
    if (paused) return undefined;
    const interval = setInterval(next, autoPlayInterval);
    return () => clearInterval(interval);
  }, [paused, next, autoPlayInterval]);

  React.useEffect(() => {
    // Bound to the window because the arrows should work without hunting for
    // something to focus first — but that means it has to decline the two
    // cases where it would be stealing the key: the section is not on screen,
    // or the reader is typing.
    if (!onScreen) return undefined;

    const handleKeyDown = (event) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      if (event.key === "ArrowLeft") {
        setEngaged(true);
        prev();
      } else if (event.key === "ArrowRight") {
        setEngaged(true);
        next();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onScreen, next, prev]);

  if (!items.length || !activeItem) return null;

  const ActiveIcon = activeItem.icon;
  const spin = !reduceMotion && layout.spin;

  // Built once and placed in one of two positions, so the two layouts cannot
  // drift apart.
  const card = (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeItem.id}
        initial={{ opacity: 0, y: reduceMotion ? 0 : 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: reduceMotion ? 0 : -10 }}
        transition={{ duration: 0.3 }}
        className={`relative z-20 rounded-xl bg-[var(--bg-elevated)] p-4 text-left shadow-[var(--shadow-md)] ${
          cardInside ? cardWidth : "w-full"
        }`}
      >
        {/* Name, logo, description, and pager. Per-skill years were dropped —
            they invited scrutiny without adding signal; the section credential
            lives in the stats band below instead. */}
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--bg-recessed)] p-1.5 shadow-[var(--border-ring)]">
            {ActiveIcon && (
              <ActiveIcon className="h-full w-full text-[var(--fg-primary)]" />
            )}
          </div>
          <h3 className="t-label truncate text-[var(--fg-primary)]">
            {activeItem.name}
          </h3>
        </div>

        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[var(--fg-secondary)]">
          {activeItem.description}
        </p>

        <div className="mt-3 flex items-center justify-end border-t border-[var(--bg-recessed)] pt-2.5">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setEngaged(true);
                prev();
              }}
              aria-label="Previous skill"
              className="cursor-pointer rounded-md bg-[var(--bg-canvas)] p-1 text-[var(--fg-secondary)] shadow-[var(--border-ring)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)]"
            >
              <ChevronLeft size={13} />
            </button>

            <span className="px-1 font-mono text-[10px] text-[var(--fg-secondary)]">
              {safeIndex + 1}/{items.length}
            </span>

            <button
              type="button"
              onClick={() => {
                setEngaged(true);
                next();
              }}
              aria-label="Next skill"
              className="cursor-pointer rounded-md bg-[var(--bg-canvas)] p-1 text-[var(--fg-secondary)] shadow-[var(--border-ring)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-primary)]"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <div
      ref={containerRef}
      className={`relative my-2 flex w-full flex-col items-center justify-center overflow-visible p-2 sm:p-4 ${
        paused ? "orbit-paused" : ""
      }`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      // A keyboard reader who has tabbed in is aiming at something. Same
      // reasoning as a click: stop moving the targets.
      onFocusCapture={() => setEngaged(true)}
    >
      {/* Outer box reserves the scaled footprint so the section does not keep
          a gap the size of the unscaled orbit underneath it. */}
      <div
        style={{ width: containerSize * scale, height: containerSize * scale }}
      >
        <div
          className="relative flex items-center justify-center"
          style={{
            width: containerSize,
            height: containerSize,
            transform: scale === 1 ? undefined : `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {/* The rings the icons travel on, drawn so the orbit is legible even
            where a gap in the icons leaves a stretch of it empty. */}
          <div
            className="pointer-events-none absolute rounded-full shadow-[var(--border-ring)]"
            style={{ width: radius * 2, height: radius * 2 }}
          />
          {useTwoRings && (
            <div
              className="pointer-events-none absolute rounded-full shadow-[var(--border-ring)]"
              style={{ width: innerRadius * 2, height: innerRadius * 2 }}
            />
          )}
          <div
            className="pointer-events-none absolute rounded-full bg-[var(--bg-recessed)]"
            style={{ width: discSize, height: discSize }}
          />

          {/* When the card is not in the middle the middle is not left empty —
              the active logo takes it, at a size a phone can actually read. */}
          {cardInside ? (
            card
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeItem.id}
                initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.9 }}
                transition={{ duration: 0.25 }}
                className="relative z-20"
                style={{ width: discSize * 0.34, height: discSize * 0.34 }}
              >
                {ActiveIcon && (
                  <ActiveIcon className="h-full w-full text-[var(--fg-primary)]" />
                )}
              </motion.div>
            </AnimatePresence>
          )}

          <OrbitRing
            key={`outer:${ringKey(outerItems)}`}
            items={outerItems}
            radius={radius}
            size={outerSize}
            duration={OUTER_DURATION}
            reverse={false}
            spin={spin}
            activeId={activeItem.id}
            onSelect={selectById}
          />

          {useTwoRings && (
            <OrbitRing
              key={`inner:${ringKey(innerItems)}`}
              items={innerItems}
              radius={innerRadius}
              size={innerSize}
              duration={INNER_DURATION}
              reverse
              stagger
              spin={spin}
              activeId={activeItem.id}
              onSelect={selectById}
            />
          )}
        </div>
      </div>

      {/* Outside the scaled box on purpose: the orbit may be shrunk to fit a
          narrow screen, and shrinking body text along with it is how a phone
          layout ends up at nine-point type. */}
      {!cardInside && <div className="mt-4 w-full max-w-[300px]">{card}</div>}

      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => select(index)}
            aria-label={`Show ${item.name}`}
            className={`h-1 cursor-pointer rounded-full transition-all ${
              index === safeIndex
                ? "w-4 bg-[var(--fg-primary)]"
                : "w-1.5 bg-[var(--fg-muted)]"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
